import { useQuery } from '@tanstack/react-query';

import { usePartySizeRange } from 'reservations/account/providers/ConfigProvider';
import { AVAILABILITY_STATUS } from 'reservations/availabilities/constants';
import { fetchAvailabilities } from 'reservations/availabilities/queries/fetchAvailabilities';
import type {
  GetAvailabilities,
  Slot,
  TimeSlots,
  UseAvailabilitiesQuery
} from 'reservations/availabilities/types';
import { HTTP_STATUS_CODE_403_UNAUTHORIZED } from 'reservations/base/constants/httpStatusCodes';
import { DEFAULT_QUERY_OPTIONS } from 'reservations/base/constants/queryOptions';
import { FORCE_REDIRECT_ARGS } from 'reservations/base/constants/redirects';
import { REDIRECT_WITH_ACCOUNT_DETAILS } from 'reservations/base/constants/redirects';
import { useCustomRouter } from 'reservations/base/providers/RouterProvider';
import type { RequestError } from 'utility/restClient';

function getMinuteIndexFromSlotSizeAndMinute(
  slotSize: number,
  minute: string
): number | undefined {
  switch (slotSize) {
    case 15: {
      switch (minute) {
        case '00':
          return 0;
        case '15':
          return 1;
        case '30':
          return 2;
        default:
          return 3;
      }
    }
    case 30: {
      switch (minute) {
        case '00':
          return 0;
        default:
          return 1;
      }
    }
    default:
      return 0;
  }
}

function buildTimeSlots(slots: Slot[], slotSize: number): (Slot | null)[] {
  const slotsByTimeHash: Map<string, (Slot | null)[]> = new Map();
  slots.forEach(slot => {
    const time12 = slot.time12;
    const [time, amOrPm] = time12.split(' ');
    const [hour, minute] = time.split(':');

    const timeHash: string = `${hour},${amOrPm}`;
    const numCols = Math.floor(60 / slotSize);

    if (!slotsByTimeHash.has(timeHash)) {
      slotsByTimeHash.set(timeHash, new Array<Slot | null>(numCols).fill(null));
    }

    const indexOfMinute = getMinuteIndexFromSlotSizeAndMinute(slotSize, minute);

    const timeSlots = slotsByTimeHash.get(timeHash);
    if (timeSlots && indexOfMinute != null) {
      timeSlots[indexOfMinute] = slot;
    }
  });

  return [...slotsByTimeHash.values()].reduce((acc, slots) => {
    acc.push(...slots);
    return acc;
  }, []);
}

function selectTimeSlots(data: GetAvailabilities): TimeSlots {
  // NOTE: We're only ever fetching a day's worth of slots with this query
  const slots = data.availability?.[0]?.slots ?? [];
  const slotSize = data.slotSize;

  // NOTE: if slots are empty or if the status of an availability
  // is not AVAILABILITY_STATUS.AVAILABILE, then we can consider it empty
  const isEmpty =
    !slots.length ||
    data.availability[0].status !== AVAILABILITY_STATUS.AVAILABLE;

  return {
    isEmpty,
    slots: buildTimeSlots(slots, slotSize)
  };
}

function useAvailabilitiesQuery({
  partySize,
  date,
  apiKey,
  slug
}: UseAvailabilitiesQuery) {
  const { handleNavigation } = useCustomRouter();
  const { max } = usePartySizeRange();

  return useQuery<GetAvailabilities, RequestError, TimeSlots>({
    ...DEFAULT_QUERY_OPTIONS,
    queryKey: ['bentoboxReservations', 'availabilities', slug, partySize, date],
    queryFn: () =>
      fetchAvailabilities({
        partySize,
        apiKey,
        beginDateTime: date,
        endDateTime: date
      }),
    refetchOnWindowFocus: true,
    enabled: !!slug && !!apiKey && !!date && !!partySize && partySize <= max,
    select: (data: GetAvailabilities) => selectTimeSlots(data),
    meta: {
      onQueryError: (error: RequestError) => {
        if (error.code === HTTP_STATUS_CODE_403_UNAUTHORIZED) {
          // NOTE: This means that we are force reloading the page
          handleNavigation(...FORCE_REDIRECT_ARGS);
          return;
        }

        handleNavigation(REDIRECT_WITH_ACCOUNT_DETAILS);
      }
    }
  });
}

export { useAvailabilitiesQuery };
